import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";

export default function CC414({ submitForm, declarationInfo }) {
  const [loading, setLoading] = useState(true);

  // Message fields
  const [messageSender, setMessageSender] = useState("");
  const [messageRecipient, setMessageRecipient] = useState("");

  // Invalidation request fields
  const [invalidationRequestDate, setInvalidationRequestDate] = useState("");
  const [invalidationReason, setInvalidationReason] = useState("");

  // Declarant fields
  const [declarantIdentificationNumber, setDeclarantIdentificationNumber] =
    useState("");
  const [declarantName, setDeclarantName] = useState("");
  const [streetAndNumber, setStreetAndNumber] = useState("");
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [country, setCountry] = useState("");

  // Customs office
  const [customsOffice, setCustomsOffice] = useState("");

  useEffect(() => {
    if (declarationInfo?.declaration_info) {
      setMessageSender(declarationInfo.declaration_info.message_sender || "");
      setMessageRecipient(
        declarationInfo.declaration_info.message_recipient || ""
      );

      setDeclarantIdentificationNumber(
        declarationInfo.declaration_info.declarant_identification || ""
      );
      setCustomsOffice(
        declarationInfo.declaration_info.customs_office_reference || ""
      );
    }

    const today = new Date().toISOString().split("T")[0];
    setInvalidationRequestDate(today);
    setLoading(false);
  }, [declarationInfo]);

  const handleSubmit = () => {
    const requestData = {
      invalidation_request_date_and_time: invalidationRequestDate,
      invalidation_reason: invalidationReason,
      declarant_identification_number: declarantIdentificationNumber,
      declarant_name: declarantName,
      street_and_number: streetAndNumber,
      city: city,
      postcode: postcode,
      country: country,
      customs_office: customsOffice,
    };

    submitForm({ messageType: "CC414", data: requestData });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div style={{ margin: "20px 0" }}>
      <Paper
        sx={{ boxShadow: "none" }}
        style={{
          padding: "20px",
          marginBottom: "20px",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Declaration Information
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Message Sender"
              value={messageSender}
              onChange={(e) => setMessageSender(e.target.value)}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Message Recipient"
              value={messageRecipient}
              onChange={(e) => setMessageRecipient(e.target.value)}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              MRN
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" style={{ fontWeight: 500 }}>
                {declarationInfo?.mrn || "N/A"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Message Type
            </Typography>
            <Chip label="CC414 - H1" color="error" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Preparation Date & Time
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Will be automatically set to current moment when submitting
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          CC414 - Cerere de invalidare
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Invalidation Request Date"
              type="date"
              value={invalidationRequestDate}
              onChange={(e) => setInvalidationRequestDate(e.target.value)}
              fullWidth
              required
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Invalidation Reason"
              value={invalidationReason}
              onChange={(e) => setInvalidationReason(e.target.value)}
              fullWidth
              required
              multiline
              rows={4}
              placeholder="Enter the reason for invalidation request..."
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          Declarant Information
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Declarant Identification Number"
              value={declarantIdentificationNumber}
              onChange={(e) => setDeclarantIdentificationNumber(e.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Declarant Name"
              value={declarantName}
              onChange={(e) => setDeclarantName(e.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Street and Number"
              value={streetAndNumber}
              onChange={(e) => setStreetAndNumber(e.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Postcode"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              fullWidth
              required
              placeholder="RO"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          Customs Office
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Customs Office"
              value={customsOffice}
              onChange={(e) => setCustomsOffice(e.target.value)}
              fullWidth
              required
              placeholder="ROCR9000"
            />
          </Grid>
        </Grid>
      </Paper>

      <Box display="flex" gap={2} justifyContent="center" mt={3}>
        <Button
          variant="contained"
          color="error"
          startIcon={<SendIcon />}
          onClick={handleSubmit}
        >
          Trimite CC414
        </Button>
      </Box>
    </div>
  );
}
